import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Radar, Users, Columns3, Building2, CalendarCheck, BarChart3, Settings, Search, Plus, Bell,
  Menu, LogOut, UserPlus, CalendarPlus, Upload } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/context/MetaContext";
import { api, fromNow, initials } from "@/lib/api";
import { Wordmark } from "@/pages/Login";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
  DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import AddLeadDialog from "@/components/crm/AddLeadDialog";
import TaskDialog from "@/components/crm/TaskDialog";

const NAV = [
  ["/", "Overview", LayoutDashboard], ["/lead-engine", "Lead Engine", Radar], ["/leads", "All Leads", Users],
  ["/pipeline", "Pipeline View", Columns3, true], ["/customers", "Customers", Building2], ["/tasks", "Follow-ups & Tasks", CalendarCheck],
  ["/reports", "Reports", BarChart3], ["/settings", "Settings", Settings],
];
const ROLE = { admin: "Admin", manager: "Manager", sales: "Sales" };

const Avatar = ({ user, size = 8 }) => user?.picture
  ? <img src={user.picture} alt="" className={`h-${size} w-${size} rounded-full object-cover`} />
  : <div className={`h-${size} w-${size} rounded-full bg-[#ECF5F1] text-[#0B3C2D] grid place-items-center text-xs font-semibold`}>{initials(user?.name)}</div>;

function SidebarBody({ onNavigate }) {
  const { user, logout } = useAuth();
  return (
    <div className="flex h-full flex-col">
      <div className="px-5 h-16 flex items-center border-b border-gray-100"><Wordmark /></div>
      <nav className="flex-1 px-3 py-4 space-y-0.5">
        {NAV.map(([to, label, Icon, highlight]) => (
          <NavLink key={to} to={to} end={to === "/"} onClick={onNavigate}
            data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2 text-[13.5px] transition-colors ${isActive
              ? "bg-[#ECF5F1] text-[#0B3C2D] font-semibold" : highlight ? "bg-[#F3FAF7] ring-1 ring-inset ring-[#A3D9C9] text-[#0B3C2D] font-semibold hover:bg-[#ECF5F1]" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"}`}>
            {({ isActive }) => (<><Icon className={`h-[17px] w-[17px] ${isActive || highlight ? "text-[#0B3C2D]" : "text-gray-400"}`} strokeWidth={1.8} />{label}
              {highlight && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-emerald-500" />}</>)}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-gray-100">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="sidebar-profile-button" className="w-full flex items-center gap-3 rounded-lg p-2 hover:bg-gray-50 text-left">
              <Avatar user={user} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{user?.name}</div>
                <div className="text-[11px] text-gray-500">{ROLE[user?.role]}</div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuLabel className="text-xs font-normal text-gray-500 truncate">{user?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="sidebar-logout" onClick={logout}><LogOut className="h-4 w-4 mr-2" />Sign out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const ref = useRef();
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return; }
    const t = setTimeout(() => api.get("/leads", { params: { q, limit: 6 } }).then((r) => setRes(r.data.items)).catch(() => {}), 220);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    const k = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); ref.current?.focus(); } };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, []);
  const go = (path) => { setOpen(false); setQ(""); nav(path); };
  return (
    <div className="relative flex-1 max-w-md">
      <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
      <input ref={ref} data-testid="global-search-input" value={q} placeholder="Search companies, contacts, phone…"
        onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => e.key === "Enter" && q.trim() && go(`/leads?q=${encodeURIComponent(q.trim())}`)}
        className="w-full h-9 rounded-lg bg-gray-50 border border-gray-200 pl-9 pr-12 text-sm outline-none focus:bg-white focus:border-[#A3D9C9] focus:ring-2 focus:ring-[#ECF5F1] transition-colors" />
      <kbd className="hidden sm:block absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 border rounded px-1.5 py-0.5">Ctrl K</kbd>
      {open && res.length > 0 && (
        <div className="absolute z-50 mt-1.5 w-full card-surface shadow-lg py-1.5" data-testid="global-search-results">
          {res.map((l) => (
            <button key={l.lead_id} onMouseDown={() => go(`/leads/${l.lead_id}`)} data-testid={`search-result-${l.lead_id}`}
              className="w-full text-left px-3 py-2 hover:bg-gray-50">
              <div className="text-sm font-medium">{l.company}</div>
              <div className="text-xs text-gray-500">{l.contact_person || "—"} · {l.stage_name}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Notifications() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const nav = useNavigate();
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);
  const open = async (n) => {
    await api.post(`/notifications/${n.notification_id}/read`);
    load();
    if (n.lead_id) nav(`/leads/${n.lead_id}`);
  };
  const dot = { overdue: "bg-red-500", escalation: "bg-red-500", warning: "bg-amber-500" };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="notifications-button" className="relative h-9 w-9 grid place-items-center rounded-lg hover:bg-gray-50">
          <Bell className="h-[18px] w-[18px] text-gray-600" strokeWidth={1.8} />
          {data.unread > 0 && <span data-testid="notifications-unread-count" className="absolute top-1 right-1 min-w-4 h-4 px-1 rounded-full bg-red-600 text-white text-[10px] grid place-items-center">{data.unread > 9 ? "9+" : data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="text-sm font-semibold">Notifications</span>
          <button data-testid="notifications-mark-all-read" className="text-xs text-[#0B3C2D] hover:underline"
            onClick={() => api.post("/notifications/read-all").then(load)}>Mark all read</button>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {data.items.length === 0 && <p className="text-sm text-gray-500 p-6 text-center">You're all caught up.</p>}
          {data.items.map((n) => (
            <button key={n.notification_id} onClick={() => open(n)} data-testid={`notification-${n.notification_id}`}
              className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 flex gap-3 ${n.read ? "opacity-60" : ""}`}>
              <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${dot[n.kind] || "bg-[#0B3C2D]"}`} />
              <span className="min-w-0">
                <span className="block text-[13px] font-medium">{n.title}</span>
                <span className="block text-xs text-gray-500 mt-0.5">{n.body}</span>
                <span className="block text-[11px] text-gray-400 mt-1">{fromNow(n.created_at)}</span>
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function AppLayout({ children }) {
  const [mobile, setMobile] = useState(false);
  const { user } = useAuth();
  const { addLeadOpen, setAddLeadOpen, taskOpen, setTaskOpen, bump } = useMeta();
  const nav = useNavigate();
  return (
    <div className="min-h-screen flex">
      <aside className="hidden lg:block print:!hidden w-60 shrink-0 bg-white border-r border-gray-200 fixed inset-y-0 left-0" data-testid="sidebar">
        <SidebarBody />
      </aside>
      <Sheet open={mobile} onOpenChange={setMobile}>
        <SheetContent side="left" className="p-0 w-64"><SidebarBody onNavigate={() => setMobile(false)} /></SheetContent>
      </Sheet>
      <div className="flex-1 lg:pl-60 print:!pl-0 min-w-0">
        <header className="print:hidden sticky top-0 z-30 h-14 bg-white/90 backdrop-blur border-b border-gray-200 flex items-center gap-3 px-4 sm:px-6">
          <button className="lg:hidden h-9 w-9 grid place-items-center rounded-lg hover:bg-gray-50" onClick={() => setMobile(true)} data-testid="mobile-menu-button">
            <Menu className="h-5 w-5" />
          </button>
          <GlobalSearch />
          <div className="ml-auto flex items-center gap-1.5">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="quick-add-button" size="sm" className="h-9 bg-[#0B3C2D] hover:bg-[#072B20] rounded-lg">
                  <Plus className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">New</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem data-testid="quick-add-lead" onClick={() => setAddLeadOpen(true)}><UserPlus className="h-4 w-4 mr-2" />Add lead</DropdownMenuItem>
                <DropdownMenuItem data-testid="quick-add-task" onClick={() => setTaskOpen(true)}><CalendarPlus className="h-4 w-4 mr-2" />Schedule follow-up</DropdownMenuItem>
                <DropdownMenuItem data-testid="quick-import" onClick={() => nav("/lead-engine")}><Upload className="h-4 w-4 mr-2" />Import leads</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Notifications />
            <button onClick={() => nav("/settings")} className="hidden sm:block ml-1" data-testid="topbar-profile"><Avatar user={user} /></button>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 print:!p-0 max-w-[1440px] mx-auto">{children}</main>
      </div>
      <AddLeadDialog open={addLeadOpen} onOpenChange={setAddLeadOpen} onCreated={(l) => { bump(); nav(`/leads/${l.lead_id}`); }} />
      <TaskDialog open={taskOpen} onOpenChange={setTaskOpen} onSaved={bump} />
    </div>
  );
}
